import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
API.interceptors.request.use(cfg => {
  try { const u = JSON.parse(localStorage.getItem('npd_user')); if (u?.token) cfg.headers.Authorization = 'Bearer ' + u.token; } catch {}
  return cfg;
});

// GSO/FDA-style daily values (2000 kcal basis)
const DV = { energy_kcal: 2000, fat: 78, sat_fat: 20, carbs: 275, sugars: null, added_sugars: 50, fiber: 28, protein: 50, sodium_mg: 2300, salt_g: 5.75 };

const ROWS = [
  { key: 'energy_kcal', label: 'Energy', unit: 'kcal', bold: true },
  { key: 'energy_kj', label: '', unit: 'kJ', indent: true },
  { key: 'fat', label: 'Total Fat', unit: 'g', bold: true },
  { key: 'sat_fat', label: 'Saturated Fat', unit: 'g', indent: true },
  { key: 'trans_fat', label: 'Trans Fat', unit: 'g', indent: true },
  { key: 'carbs', label: 'Total Carbohydrate', unit: 'g', bold: true },
  { key: 'sugars', label: 'Total Sugars', unit: 'g', indent: true },
  { key: 'added_sugars', label: 'Added Sugars', unit: 'g', indent: true },
  { key: 'fiber', label: 'Dietary Fiber', unit: 'g', indent: true },
  { key: 'protein', label: 'Protein', unit: 'g', bold: true },
  { key: 'sodium_mg', label: 'Sodium', unit: 'mg', bold: true },
  { key: 'salt_g', label: 'Salt', unit: 'g', indent: true },
];

export default function NutritionTab({ product }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!product?.product_code) return;
    API.get('/nutrition/resolve/' + encodeURIComponent(product.product_code))
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.error || 'Failed to resolve nutrition'));
  }, [product?.product_code]);

  if (err) return <div className="card"><div style={{ color: 'var(--red)' }}>{err}</div></div>;
  if (!data) return <div className="card">Loading nutrition…</div>;
  if (!data.profile) return <div className="card">No recipe found — nutrition is computed from the recipe. Add a recipe first.</div>;

  const p = data.profile;
  const kf = data.known_fraction || 0;

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {/* Label-style panel */}
      <div className="card" style={{ width: 340 }}>
        <div style={{ border: '2px solid var(--text, #111)', padding: 12, fontFamily: 'Helvetica, Arial, sans-serif' }}>
          <div style={{ fontSize: 22, fontWeight: 900, borderBottom: '8px solid var(--text, #111)', paddingBottom: 4 }}>Nutrition Facts</div>
          <div style={{ fontSize: 12, padding: '4px 0', borderBottom: '4px solid var(--text, #111)' }}>Per 100 g (estimated)</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr><th></th><th></th><th style={{ textAlign: 'right', fontSize: 10, paddingTop: 4 }}>% DV*</th></tr>
            </thead>
            <tbody>
              {ROWS.map(r => {
                const val = p[r.key];
                if (val === undefined || val === null) return null;
                const dv = DV[r.key] ? Math.round((val / DV[r.key]) * 100) : null;
                return (
                  <tr key={r.key} style={{ borderTop: '1px solid #999' }}>
                    <td style={{ padding: '3px 0', paddingLeft: r.indent ? 14 : 0, fontWeight: r.bold ? 800 : 400 }}>{r.label}</td>
                    <td style={{ textAlign: 'right', padding: '3px 4px' }}>{val}{r.unit}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, width: 46 }}>{dv !== null ? dv + '%' : ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ fontSize: 9, borderTop: '4px solid var(--text, #111)', paddingTop: 4, marginTop: 4 }}>
            * Percent Daily Values based on a 2,000 kcal diet.
          </div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.5 }}>
          ⚠ {data.disclaimer}
        </div>
      </div>

      {/* Composition breakdown */}
      <div className="card" style={{ flex: 1, minWidth: 320 }}>
        <div className="card-title">Computed from recipe</div>
        <div style={{ margin: '8px 0 14px' }}>
          <div style={{ fontSize: 12, marginBottom: 4 }}>
            Data coverage: <b>{Math.round(kf * 100)}%</b> of recipe mass has nutrition data
            {!data.known && <span style={{ color: 'var(--red)', marginLeft: 8 }}>· incomplete — values understate reality</span>}
          </div>
          <div style={{ height: 8, background: 'var(--bg-elevated, #eee)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: (kf * 100) + '%', height: '100%', background: data.known ? 'var(--green, #4caf50)' : 'var(--orange, #ff9800)' }} />
          </div>
        </div>
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg-elevated)' }}>
              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Component</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', width: 80 }}>Fraction</th>
              <th style={{ textAlign: 'center', padding: '6px 8px', width: 90 }}>Data</th>
            </tr>
          </thead>
          <tbody>
            {data.components.map((c, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '6px 8px' }}>{c.item_name || c.item_code}</td>
                <td style={{ textAlign: 'right', padding: '6px 8px' }}>{(c.fraction * 100).toFixed(1)}%</td>
                <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                  {c.known
                    ? <span style={{ fontSize: 10, background: '#e8f5e9', color: '#2e7d32', borderRadius: 8, padding: '2px 8px' }}>{c.source}</span>
                    : <span style={{ fontSize: 10, background: '#fff3e0', color: '#e65100', borderRadius: 8, padding: '2px 8px' }}>no data</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
